import React, { useState, useEffect, useCallback } from 'react';
import { RotateCcw, CheckCircle, XCircle, RotateCw, Move, ZoomIn, ZoomOut } from 'lucide-react';
import { Resource, UIComponent } from '../../types';

interface ObjectManipulatorComponentProps {
  resource: Resource;
  component: UIComponent;
  onAction?: (action: string, data?: any) => void;
}

interface ManipulableObject {
  id: string;
  type: string;
  x: number;
  y: number;
  rotation: number;
  scale: number;
  color: string;
  isSelected: boolean;
  isDragging: boolean;
  originalX: number;
  originalY: number;
}

interface Transformation {
  type: 'move' | 'rotate' | 'scale';
  value: number;
  direction?: 'left' | 'right' | 'up' | 'down';
}

export const ObjectManipulatorComponent: React.FC<ObjectManipulatorComponentProps> = ({
  resource, 
  component, 
  onAction 
}) => {
  const [objects, setObjects] = useState<ManipulableObject[]>([]);
  const [selectedObject, setSelectedObject] = useState<ManipulableObject | null>(null);
  const [selectedTool, setSelectedTool] = useState<string>('select');
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackType, setFeedbackType] = useState<'success' | 'error'>('success');
  const [currentInstruction, setCurrentInstruction] = useState<string>('');
  const [instructionIndex, setInstructionIndex] = useState(0);
  const [validationResults, setValidationResults] = useState<{[key: string]: boolean}>({});
  const [showValidation, setShowValidation] = useState(false);

  // Get configuration from resource
  const canvasWidth = 600;
  const canvasHeight = 400;
  const objectSize = 40;
  
  // Get settings from resource
  const allowInteraction = resource.allowInteraction !== undefined ? resource.allowInteraction : true;
  const question = resource.question || 'Manipulez les objets selon les consignes';
  
  // Get objects and transformations from resource

  const generateObjects = useCallback(() => {
    const objectConfigs: ManipulableObject[] = resource.objects || [];
    const newObjects: ManipulableObject[] = objectConfigs.map((config, index) => ({
      ...config,
      id: config.id || `object_${index}`,
      x: 100 + index * 80,
      y: 100 + (index % 2) * 80,
      rotation: 0,
      scale: 1,
      isSelected: false,
      isDragging: false,
      originalX: 100 + index * 80,
      originalY: 100 + (index % 2) * 80
    }));
    
    setObjects(newObjects);
  }, [resource.objects]);

  const generateInstructions = useCallback(() => {
    const instructions = [
      'Sélectionnez l\'objet rouge',
      'Faites-le pivoter de 90 degrés',
      'Agrandissez l\'objet bleu',
      'Déplacez l\'objet vert vers la droite'
    ];
    
    if (objects.length > 0) {
      setCurrentInstruction(instructions[instructionIndex] || instructions[0]);
    }
  }, [objects, instructionIndex]);

  const handleObjectClick = (object: ManipulableObject) => {
    if (!allowInteraction) return;
    
    // Deselect all objects
    setObjects(prev => prev.map(obj => ({ ...obj, isSelected: false })));
    
    // Select clicked object
    const updatedObject = { ...object, isSelected: true };
    setObjects(prev => prev.map(obj => obj.id === object.id ? updatedObject : obj));
    setSelectedObject(updatedObject);
    
    onAction?.('object_selected', { objectId: object.id, type: object.type });
  };

  const handleObjectDrag = (object: ManipulableObject, newX: number, newY: number) => {
    if (!allowInteraction || selectedTool !== 'move') return;
    
    const updatedObject = { ...object, x: newX, y: newY };
    setObjects(prev => prev.map(obj => obj.id === object.id ? updatedObject : obj));
    
    onAction?.('object_moved', { objectId: object.id, x: newX, y: newY });
  };

  const handleObjectRotate = (object: ManipulableObject, angle: number) => {
    if (!allowInteraction || selectedTool !== 'rotate') return;
    
    const updatedObject = { ...object, rotation: (object.rotation + angle) % 360 };
    setObjects(prev => prev.map(obj => obj.id === object.id ? updatedObject : obj));
    
    onAction?.('object_rotated', { objectId: object.id, rotation: updatedObject.rotation });
  };

  const handleObjectScale = (object: ManipulableObject, scaleFactor: number) => {
    if (!allowInteraction || selectedTool !== 'scale') return;
    
    const newScale = Math.max(0.5, Math.min(2, object.scale * scaleFactor));
    const updatedObject = { ...object, scale: newScale };
    setObjects(prev => prev.map(obj => obj.id === object.id ? updatedObject : obj));
    
    onAction?.('object_scaled', { objectId: object.id, scale: newScale });
  };

  const applyTransformation = (transformation: Transformation) => {
    if (!selectedObject) {
      showFeedbackMessage('Sélectionnez d\'abord un objet !', 'error');
      return;
    }

    switch (transformation.type) {
      case 'move':
        if (transformation.direction === 'left') {
          handleObjectDrag(selectedObject, selectedObject.x - 50, selectedObject.y);
        } else if (transformation.direction === 'right') {
          handleObjectDrag(selectedObject, selectedObject.x + 50, selectedObject.y);
        } else if (transformation.direction === 'up') {
          handleObjectDrag(selectedObject, selectedObject.x, selectedObject.y - 50);
        } else if (transformation.direction === 'down') {
          handleObjectDrag(selectedObject, selectedObject.x, selectedObject.y + 50);
        }
        break;
      
      case 'rotate':
        handleObjectRotate(selectedObject, transformation.value);
        break;
      
      case 'scale':
        handleObjectScale(selectedObject, transformation.value);
        break;
    }

    showFeedbackMessage('Transformation appliquée !', 'success');
    
    // Move to next instruction
    if (instructionIndex < 3) {
      setInstructionIndex(prev => prev + 1);
      setTimeout(() => {
        generateInstructions();
      }, 1000);
    }
  };

  const showFeedbackMessage = (message: string, type: 'success' | 'error') => {
    setFeedbackMessage(message);
    setFeedbackType(type);
    setShowFeedback(true);
    
    setTimeout(() => {
      setShowFeedback(false);
    }, 2000);
  };


  const validateAllTransformations = (): {[key: string]: boolean} => {
    const results: {[key: string]: boolean} = {};
    
    objects.forEach(object => {
      const expectedTransformations = resource.transformations || [];
      const expectedTransformation = expectedTransformations.find((t: any) => t.objectId === object.id);
      
      if (expectedTransformation) {
        // Check if object meets all expected criteria
        let isValid = true;
        
        if (expectedTransformation.expectedPosition) {
          const tolerance = 30;
          const positionValid = Math.abs(object.x - expectedTransformation.expectedPosition.x) <= tolerance && 
                               Math.abs(object.y - expectedTransformation.expectedPosition.y) <= tolerance;
          isValid = isValid && positionValid;
        }
        
        if (expectedTransformation.expectedRotation !== undefined) {
          const tolerance = 15;
          const rotationValid = Math.abs(object.rotation - expectedTransformation.expectedRotation) <= tolerance;
          isValid = isValid && rotationValid;
        }
        
        if (expectedTransformation.expectedScale !== undefined) {
          const tolerance = 0.1;
          const scaleValid = Math.abs(object.scale - expectedTransformation.expectedScale) <= tolerance;
          isValid = isValid && scaleValid;
        }
        
        results[object.id] = isValid;
      } else {
        results[object.id] = true; // No validation required
      }
    });
    
    return results;
  };

  const handleValidation = () => {
    const results = validateAllTransformations();
    setValidationResults(results);
    setShowValidation(true);
    
    const allValid = Object.values(results).every(valid => valid);
    if (allValid) {
      showFeedbackMessage('Excellent ! Toutes les transformations sont correctes !', 'success');
    } else {
      showFeedbackMessage('Certaines transformations ne sont pas correctes. Vérifiez les objets marqués en rouge.', 'error');
    }
  };

  const resetCanvas = () => {
    generateObjects();
    setInstructionIndex(0);
    setSelectedObject(null);
    setSelectedTool('select');
    setValidationResults({});
    setShowValidation(false);
    onAction?.('canvas_reset', {});
  };

  const getObjectIcon = (object: ManipulableObject) => {
    const iconMap: Record<string, string> = {
      'circle': '●',
      'square': '■',
      'triangle': '▲',
      'star': '⭐',
      'diamond': '◆',
      'hexagon': '⬡'
    };
    return iconMap[object.type] || '●';
  };

  const getObjectColor = (object: ManipulableObject) => {
    const colorMap: Record<string, string> = {
      'red': '#EF4444',
      'blue': '#3B82F6',
      'green': '#10B981',
      'yellow': '#F59E0B',
      'purple': '#8B5CF6',
      'orange': '#F97316'
    };
    return colorMap[object.color] || object.color;
  };

  useEffect(() => {
    generateObjects();
  }, [generateObjects]);

  useEffect(() => {
    generateInstructions();
  }, [generateInstructions]);

  return (
    <div className="object-manipulator-component">
      {/* Header */}
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          {resource.name || 'Manipulateur d\'Objets'}
        </h3>
        <p className="text-sm text-gray-600 mb-2">
          {resource.description || 'Manipulez, faites pivoter et redimensionnez les objets'}
        </p>
        {question && (
          <p className="text-md font-medium text-blue-600 mb-2">
            {question}
          </p>
        )}
        <div className="flex items-center space-x-4 text-sm text-gray-600">
          <span>Outil: {selectedTool === 'select' ? 'Sélection' : selectedTool === 'move' ? 'Déplacement' : selectedTool === 'rotate' ? 'Rotation' : 'Redimensionnement'}</span>
          <button
            onClick={resetCanvas}
            className="flex items-center space-x-1 text-blue-600 hover:text-blue-800"
          >
            <RotateCcw className="w-4 h-4" />
            <span>Réinitialiser</span>
          </button>
          <button
            onClick={handleValidation}
            className="flex items-center space-x-1 text-green-600 hover:text-green-800"
          >
            <CheckCircle className="w-4 h-4" />
            <span>Valider</span>
          </button>
        </div>
      </div>

      {/* Current Instruction */}
      {currentInstruction && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-center space-x-2">
            <span className="font-medium text-yellow-800">{currentInstruction}</span>
          </div>
        </div>
      )}

      {/* Canvas */}
      <div className="flex space-x-6">
        {/* Main Canvas */}
        <div className="flex-shrink-0">
          <div 
            className="relative border-2 border-gray-300 rounded-lg bg-gray-50"
            style={{ width: canvasWidth, height: canvasHeight }}
          >
            {objects.map((object) => (
              <div
                key={object.id}
                className={`absolute cursor-pointer transition-all duration-200 ${
                  object.isSelected ? 'ring-4 ring-blue-500' : ''
                } ${
                  showValidation && validationResults[object.id] === false ? 'ring-4 ring-red-500' : ''
                } ${
                  showValidation && validationResults[object.id] === true ? 'ring-4 ring-green-500' : ''
                }`}
                style={{
                  left: object.x,
                  top: object.y,
                  width: objectSize * object.scale,
                  height: objectSize * object.scale,
                  transform: `rotate(${object.rotation}deg)`,
                  transformOrigin: 'center'
                }}
                onClick={() => handleObjectClick(object)}
              >
                <div
                  className="w-full h-full flex items-center justify-center text-white font-bold border-2 border-gray-800 rounded-lg"
                  style={{ backgroundColor: getObjectColor(object) }}
                >
                  <span style={{ fontSize: `${Math.max(16, 24 * object.scale)}px` }}>
                    {getObjectIcon(object)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Control Panel */}
        <div className="flex-shrink-0 space-y-6">
          {/* Tools */}
          <div>
            <h4 className="font-medium text-gray-900 mb-3">Outils:</h4>
            <div className="space-y-2">
              {['select', 'move', 'rotate', 'scale'].map((tool) => (
                <button
                  key={tool}
                  onClick={() => setSelectedTool(tool)}
                  className={`w-full p-2 text-left rounded-lg transition-colors ${
                    selectedTool === tool
                      ? 'bg-blue-100 text-blue-600 border border-blue-300'
                      : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    {tool === 'select' && <Move className="w-4 h-4" />}
                    {tool === 'move' && <Move className="w-4 h-4" />}
                    {tool === 'rotate' && <RotateCw className="w-4 h-4" />}
                    {tool === 'scale' && <ZoomIn className="w-4 h-4" />}
                    <span className="capitalize">{tool}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Transformations */}
          <div>
            <h4 className="font-medium text-gray-900 mb-3">Transformations:</h4>
            <div className="space-y-2">
              {/* Move Controls */}
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => applyTransformation({ type: 'move', direction: 'up', value: 50 })}
                  className="p-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                >
                  ↑
                </button>
                <button
                  onClick={() => applyTransformation({ type: 'move', direction: 'down', value: 50 })}
                  className="p-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                >
                  ↓
                </button>
                <button
                  onClick={() => applyTransformation({ type: 'move', direction: 'left', value: 50 })}
                  className="p-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                >
                  ←
                </button>
                <button
                  onClick={() => applyTransformation({ type: 'move', direction: 'right', value: 50 })}
                  className="p-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                >
                  →
                </button>
              </div>

              {/* Rotate Controls */}
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => applyTransformation({ type: 'rotate', value: -90 })}
                  className="p-2 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                >
                  <RotateCw className="w-4 h-4 mx-auto" />
                  -90°
                </button>
                <button
                  onClick={() => applyTransformation({ type: 'rotate', value: 90 })}
                  className="p-2 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                >
                  <RotateCw className="w-4 h-4 mx-auto" />
                  +90°
                </button>
              </div>

              {/* Scale Controls */}
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => applyTransformation({ type: 'scale', value: 1.2 })}
                  className="p-2 bg-green-100 text-green-700 rounded hover:bg-green-200"
                >
                  <ZoomIn className="w-4 h-4 mx-auto" />
                  Agrandir
                </button>
                <button
                  onClick={() => applyTransformation({ type: 'scale', value: 0.8 })}
                  className="p-2 bg-green-100 text-green-700 rounded hover:bg-green-200"
                >
                  <ZoomOut className="w-4 h-4 mx-auto" />
                  Réduire
                </button>
              </div>
            </div>
          </div>

          {/* Objects List */}
          <div>
            <h4 className="font-medium text-gray-900 mb-3">Objets:</h4>
            <div className="space-y-2">
              {objects.map((object) => (
                <div
                  key={object.id}
                  className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    object.isSelected
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  } ${
                    showValidation && validationResults[object.id] === false ? 'border-red-500 bg-red-50' : ''
                  } ${
                    showValidation && validationResults[object.id] === true ? 'border-green-500 bg-green-50' : ''
                  }`}
                  onClick={() => handleObjectClick(object)}
                >
                  <div className="flex items-center space-x-3">
                    <div 
                      className="w-6 h-6 rounded flex items-center justify-center text-white text-sm"
                      style={{ backgroundColor: getObjectColor(object) }}
                    >
                      {getObjectIcon(object)}
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">
                        {object.type.charAt(0).toUpperCase() + object.type.slice(1)} {object.color}
                      </div>
                      <div className="text-xs text-gray-600">
                        R: {object.rotation}° S: {object.scale.toFixed(1)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Validation Summary */}
      {showValidation && (
        <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <h4 className="font-medium text-gray-900 mb-3">Résultats de validation:</h4>
          <div className="space-y-2">
            {objects.map((object) => {
              const isValid = validationResults[object.id];
              return (
                <div key={object.id} className="flex items-center space-x-3">
                  <div 
                    className="w-6 h-6 rounded flex items-center justify-center text-white text-sm"
                    style={{ backgroundColor: getObjectColor(object) }}
                  >
                    {getObjectIcon(object)}
                  </div>
                  <span className="text-sm text-gray-700">
                    {object.type.charAt(0).toUpperCase() + object.type.slice(1)} {object.color}
                  </span>
                  <div className="flex items-center space-x-1">
                    {isValid ? (
                      <>
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span className="text-sm text-green-600">Correct</span>
                      </>
                    ) : (
                      <>
                        <XCircle className="w-4 h-4 text-red-600" />
                        <span className="text-sm text-red-600">À corriger</span>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Feedback */}
      {showFeedback && (
        <div className={`mt-4 p-3 rounded-lg flex items-center space-x-2 ${
          feedbackType === 'success' 
            ? 'bg-green-100 text-green-800 border border-green-200' 
            : 'bg-red-100 text-red-800 border border-red-200'
        }`}>
          {feedbackType === 'success' ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <XCircle className="w-5 h-5" />
          )}
          <span>{feedbackMessage}</span>
        </div>
      )}

      {/* Instructions */}
      <div className="mt-4 p-3 bg-blue-50 rounded-lg">
        <h4 className="font-medium text-blue-900 mb-2">Instructions:</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Cliquez sur un objet pour le sélectionner</li>
          <li>• Choisissez un outil (sélection, déplacement, rotation, redimensionnement)</li>
          <li>• Utilisez les boutons de transformation pour manipuler l'objet sélectionné</li>
          <li>• Suivez les consignes données pour compléter l'exercice</li>
          <li>• Cliquez sur "Valider" pour vérifier vos transformations</li>
          <li>• Les objets corrects apparaîtront avec un contour vert, les incorrects en rouge</li>
          <li>• Utilisez le bouton "Réinitialiser" pour recommencer</li>
        </ul>
      </div>
    </div>
  );
};
